import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { getQuestions, getSmartQuestions, getWrongReviewQuestions, submitAnswer } from '../api/endpoints';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available categories sorted alphabetically
const CATEGORIES = [
  { value: 'anatomy', label: 'Anatomy', subcategories: ['Upper Limb', 'Lower Limb', 'Thorax', 'Abdomen', 'Head & Neck', 'Neuroanatomy'] },
  { value: 'biochemistry', label: 'Biochemistry', subcategories: ['Metabolism', 'Enzymes', 'Genetics', 'Molecular Biology'] },
  { value: 'cardiology', label: 'Cardiology', subcategories: ['Arrhythmias', 'Heart Failure', 'Valvular Disease', 'Coronary Disease', 'Hypertension'] },
  { value: 'cardiovascular', label: 'Cardiovascular', subcategories: ['Vascular Disease', 'Peripheral Arterial', 'Venous Disorders'] },
  { value: 'dermatology', label: 'Dermatology', subcategories: ['Inflammatory', 'Infectious', 'Neoplastic', 'Autoimmune'] },
  { value: 'endocrinology', label: 'Endocrinology', subcategories: ['Diabetes', 'Thyroid', 'Adrenal', 'Pituitary', 'Calcium Disorders'] },
  { value: 'ent', label: 'ENT', subcategories: ['Ear', 'Nose', 'Throat', 'Head & Neck'] },
  { value: 'gastroenterology', label: 'Gastroenterology', subcategories: ['Oesophagus', 'Stomach', 'Small Bowel', 'Large Bowel', 'Liver', 'Pancreas'] },
  { value: 'general', label: 'General', subcategories: [] },
  { value: 'general medicine', label: 'General Medicine', subcategories: ['Acute Medicine', 'Chronic Disease', 'Geriatrics'] },
  { value: 'gynecology', label: 'Gynaecology', subcategories: ['Menstrual Disorders', 'Fertility', 'Oncology', 'Infections'] },
  { value: 'hematology', label: 'Haematology', subcategories: ['Anaemia', 'Bleeding Disorders', 'Thrombosis', 'Malignancy'] },
  { value: 'immunology', label: 'Immunology', subcategories: ['Autoimmune', 'Immunodeficiency', 'Allergy', 'Transplant'] },
  { value: 'infectious disease', label: 'Infectious Disease', subcategories: ['Bacterial', 'Viral', 'Fungal', 'Parasitic', 'Tropical'] },
  { value: 'medicine', label: 'Medicine', subcategories: [] },
  { value: 'microbiology', label: 'Microbiology', subcategories: ['Bacteriology', 'Virology', 'Mycology', 'Parasitology'] },
  { value: 'nephrology', label: 'Nephrology', subcategories: ['Acute Kidney Injury', 'Chronic Kidney Disease', 'Glomerular Disease', 'Electrolytes'] },
  { value: 'neurology', label: 'Neurology', subcategories: ['Stroke', 'Epilepsy', 'Movement Disorders', 'Dementia', 'Headache', 'Neuropathy'] },
  { value: 'neuroscience', label: 'Neuroscience', subcategories: ['Neurophysiology', 'Neuropathology'] },
  { value: 'obstetrics', label: 'Obstetrics', subcategories: ['Antenatal', 'Labour', 'Postnatal', 'High Risk Pregnancy'] },
  { value: 'ophthalmology', label: 'Ophthalmology', subcategories: ['Anterior Segment', 'Posterior Segment', 'Neuro-ophthalmology', 'Paediatric'] },
  { value: 'orthopedics', label: 'Orthopaedics', subcategories: ['Trauma', 'Spine', 'Upper Limb', 'Lower Limb', 'Paediatric'] },
  { value: 'pathology', label: 'Pathology', subcategories: ['General Pathology', 'Systemic Pathology', 'Histopathology'] },
  { value: 'pediatrics', label: 'Paediatrics', subcategories: ['Neonatology', 'Growth & Development', 'Respiratory', 'Cardiology', 'Gastro'] },
  { value: 'pharmacology', label: 'Pharmacology', subcategories: ['Pharmacokinetics', 'Pharmacodynamics', 'Drug Classes', 'Toxicology'] },
  { value: 'physiology', label: 'Physiology', subcategories: ['Cardiovascular', 'Respiratory', 'Renal', 'Neurophysiology', 'Endocrine'] },
  { value: 'psychiatry', label: 'Psychiatry', subcategories: ['Mood Disorders', 'Psychosis', 'Anxiety', 'Personality', 'Substance Use'] },
  { value: 'radiology', label: 'Radiology', subcategories: ['X-Ray', 'CT', 'MRI', 'Ultrasound', 'Nuclear Medicine'] },
  { value: 'renal', label: 'Renal', subcategories: ['Acute Kidney Injury', 'Chronic Kidney Disease', 'Dialysis'] },
  { value: 'respiratory', label: 'Respiratory', subcategories: ['Asthma', 'COPD', 'Infections', 'Interstitial', 'Malignancy', 'Sleep'] },
  { value: 'rheumatology', label: 'Rheumatology', subcategories: ['Inflammatory Arthritis', 'Connective Tissue', 'Vasculitis', 'Osteoarthritis'] },
  { value: 'surgery', label: 'Surgery', subcategories: ['General', 'Vascular', 'Colorectal', 'Upper GI', 'Breast', 'Endocrine'] },
  { value: 'urology', label: 'Urology', subcategories: ['Prostate', 'Bladder', 'Kidney', 'Male Health', 'Paediatric'] },
].sort((a, b) => a.label.localeCompare(b.label));

const QuestionsPage = () => {
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockStatus, setUnlockStatus] = useState(null);
  const { tenant } = useTenant();
  
  // Mobile filter drawer state - check URL params
  const urlParams = new URLSearchParams(location.search);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(urlParams.get('showFilters') === 'true');
  
  // Subscription status for source filter
  const [subscriptionPlan, setSubscriptionPlan] = useState('free');
  
  // State for session stats modal
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Session summary celebration modal
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [sessionSummaryData, setSessionSummaryData] = useState(null);
  
  // Get tenant branding
  const tenantName = tenant?.name || 'MedMCQ';
  const supportEmail = tenant?.support_email || 'support@medmcq.com.au';
  const primaryColor = tenant?.primary_color || '#0891b2';
  const secondaryColor = tenant?.secondary_color || '#7c3aed';
  
  // Update page title
  useEffect(() => {
    document.title = `Questions | ${tenantName}`;
  }, [tenantName]);
  
  // Fetch subscription status
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await axios.get(`${BACKEND_URL}/api/user/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscriptionPlan(response.data?.subscription_plan || 'free');
      } catch (error) {
        console.error('Error fetching subscription:', error);
        setSubscriptionPlan('free');
      }
    };
    fetchSubscription();
  }, []);
  
  // Check if user has paid subscription (not free)
  const hasPaidSubscription = subscriptionPlan && subscriptionPlan !== 'free';
  
  // Initialize stats from localStorage to persist across navigation.
  // Reset automatically at the start of each new day so yesterday's
  // numbers don't carry over.
  const [stats, setStats] = useState(() => {
    const savedStats = localStorage.getItem('sessionStats');
    if (savedStats) {
      try {
        const parsed = JSON.parse(savedStats);
        // Check if the saved date matches today — reset if it's a new day
        const today = new Date().toISOString().split('T')[0];
        if (parsed.date && parsed.date !== today) {
          const fresh = { correct: 0, total: 0, streak: 0, date: today };
          localStorage.setItem('sessionStats', JSON.stringify(fresh));
          return fresh;
        }
        // Ensure date is set for existing data
        if (!parsed.date) parsed.date = today;
        return parsed;
      } catch (e) {
        return { correct: 0, total: 0, streak: 0, date: new Date().toISOString().split('T')[0] };
      }
    }
    return { correct: 0, total: 0, streak: 0, date: new Date().toISOString().split('T')[0] };
  });
  
  // Track if this is the first render to avoid overwriting localStorage
  const isFirstRender = useRef(true);
  
  // Ref for auto-scroll on mobile
  const questionHeaderRef = useRef(null);

  // Ref for submit button scroll-into-view
  const submitBtnRef = useRef(null);

  // Refs for category dropdown scroll preservation
  const categoryDropdownListRef = useRef(null);
  const savedCatScrollRef = useRef(0);
  
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  
  // Filters - Multi-category and subcategory selection
  // Initialize from URL param (e.g. from Analytics "Practice Now" button)
  const [selectedCategories, setSelectedCategories] = useState(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      const match = CATEGORIES.find(c =>
        c.value.toLowerCase() === cat.toLowerCase() ||
        c.label.toLowerCase() === cat.toLowerCase()
      );
      if (match) return [match.value];
      // Category not in CATEGORIES list (e.g. clinical skills, biostatistics, genetics)
      // — still use it as a filter so Areas to Improve buttons work for all categories
      return [cat.toLowerCase()];
    }
    return [];
  });
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [difficulty, setDifficulty] = useState('all');
  const [year, setYear] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [scenarioType, setScenarioType] = useState('all');
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showComplexityDropdown, setShowComplexityDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // AI adaptive engine status
  const [aiStatus, setAiStatus] = useState('diagnostics_incomplete');
  const [diagnosticProgress, setDiagnosticProgress] = useState({ answered: 0, required: 330 });

  // Complexity tier labels - FULL names, not abbreviated
  const complexityLabels = {
    '1': 'Foundational',
    '2': 'Competent',
    '3': 'Proficient',
    '4': 'Advanced'
  };

  // Fetch unlock status on mount
  useEffect(() => {
    const fetchUnlockStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await axios.get(`${BACKEND_URL}/api/unlock-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnlockStatus(response.data);
      } catch (error) {
        console.error('Error fetching unlock status:', error);
      }
    };
    fetchUnlockStatus();
  }, []);
  
  // Re-apply category from URL param if URL changes while page is mounted
  const prevSearchRef = useRef(location.search);
  useEffect(() => {
    // Skip initial mount — state initializer already handled it
    if (prevSearchRef.current === location.search) return;
    prevSearchRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) {
      const match = CATEGORIES.find(c =>
        c.value.toLowerCase() === cat.toLowerCase() ||
        c.label.toLowerCase() === cat.toLowerCase()
      );
      // Use matched value, or fall back to raw category for categories not in the dropdown list
      setSelectedCategories([match ? match.value : cat.toLowerCase()]);
    }
  }, [location.search]);

  // Scroll restoration no longer needed — converting FilterPanel from an inline
  // component to a render function means React preserves the DOM naturally

  // State for daily limit
  const [dailyLimitStatus, setDailyLimitStatus] = useState(null);
  const [dailyLimitError, setDailyLimitError] = useState(false);

  // Fetch daily limit status
  useEffect(() => {
    const fetchDailyLimit = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await axios.get(`${BACKEND_URL}/api/questions/daily-limit`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDailyLimitStatus(response.data);
      } catch (error) {
        console.error('Error fetching daily limit:', error);
      }
    };
    fetchDailyLimit();
  }, [stats.total]);

  // Save stats to localStorage whenever they change (but not on first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    localStorage.setItem('sessionStats', JSON.stringify(stats));
  }, [stats]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) {
        setShowYearDropdown(false);
        setShowComplexityDropdown(false);
        setShowSourceDropdown(false);
        setShowScenarioDropdown(false);
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [selectedCategories, difficulty, year, sourceFilter, scenarioType]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentIndex]);

  // Check if we're in wrong answer review mode
  const isWrongReviewMode = urlParams.get('review_wrong') === 'true';

  const loadQuestions = async () => {
    setLoading(true);
    setDailyLimitError(false);
    try {
      // ── Wrong Answer Review Mode ──
      if (isWrongReviewMode) {
        const response = await getWrongReviewQuestions({ count: 50 });
        const questionsData = response.data.questions || [];
        setQuestions(questionsData);
        setCurrentIndex(0);
        setAnswered(false);
        setSelectedAnswer(null);
        setLoading(false);
        return;
      }

      const params = {
        count: 100,
        session_correct: stats.correct,
        session_total: stats.total,
        wrong_streak: 0,
      };
      if (difficulty !== 'all') params.difficulty = difficulty;
      if (year !== 'all') params.year = parseInt(year);
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (scenarioType !== 'all') params.scenario_type = scenarioType;
      if (isMobile) params.mobile_only = true;

      let questionsData;
      let smartMeta = null;

      if (selectedCategories.length === 0) {
        // No category filter — get all
        const response = await getSmartQuestions(params);
        questionsData = response.data.questions;
        smartMeta = response.data;
      } else if (selectedCategories.length === 1) {
        // Single category — pass directly
        const response = await getSmartQuestions({ ...params, category: selectedCategories[0] });
        questionsData = response.data.questions;
        smartMeta = response.data;
      } else {
        // Multiple categories — one call per category, then merge and shuffle
        const results = await Promise.all(
          selectedCategories.map(cat => getSmartQuestions({ ...params, category: cat }))
        );
        questionsData = results.flatMap(r => r.data.questions);
        // Use AI status from first response
        smartMeta = results[0]?.data;
        // Shuffle combined results so categories are interleaved
        questionsData = questionsData.sort(() => Math.random() - 0.5);
      }

      // Update AI status from response metadata
      if (smartMeta) {
        setAiStatus(smartMeta.ai_status || 'diagnostics_incomplete');
        setDiagnosticProgress(smartMeta.diagnostic_progress || { answered: 0, required: 330 });
      }

      setQuestions(questionsData);
      setCurrentIndex(0);
      setAnswered(false);
      setSelectedAnswer(null);
    } catch (error) {
      console.error('Error loading questions:', error);
      if (error.response?.status === 403) {
        setDailyLimitError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryValue) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryValue)) {
        return prev.filter(c => c !== categoryValue);
      } else {
        return [...prev, categoryValue];
      }
    });
  };

  const handleClearCategories = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setExpandedCategory(null);
  };

  const handleAnswerSelect = (index) => {
    if (answered) return;

    if (timerInterval) {
      clearInterval(timerInterval);
    }

    setSelectedAnswer(index);
    // Do NOT submit yet — user must click Submit Answer button
  };

  const handleSubmitAnswer = async () => {
    if (selectedAnswer === null || answered) return;

    const question = questions[currentIndex];
    const isCorrect = selectedAnswer === question.correct_answer;
    const questionCategory = question.category;

    setAnswered(true);

    try {
      const response = await submitAnswer({
        question_id: question.id,
        selected_answer: selectedAnswer,
        is_correct: isCorrect,
        time_taken: timeElapsed,
        local_date: new Date().toISOString().split('T')[0],
      });

      setStats(prev => ({
        correct: isCorrect ? prev.correct + 1 : prev.correct,
        total: prev.total + 1,
        streak: response.data?.current_streak || (isCorrect ? prev.streak + 1 : 0),
      }));

      if (response.data?.questions_remaining !== undefined && response.data.questions_remaining >= 0) {
        setDailyLimitStatus(prev => ({
          ...prev,
          questions_remaining: response.data.questions_remaining
        }));
      }

      const savedCategoryStats = localStorage.getItem('sessionCategoryStats');
      let categoryStats = savedCategoryStats ? JSON.parse(savedCategoryStats) : {};

      if (!categoryStats[questionCategory]) {
        categoryStats[questionCategory] = { correct: 0, total: 0 };
      }
      categoryStats[questionCategory].total += 1;
      if (isCorrect) {
        categoryStats[questionCategory].correct += 1;
      }
      localStorage.setItem('sessionCategoryStats', JSON.stringify(categoryStats));

    } catch (error) {
      console.error('Error submitting answer:', error);

      if (error.response?.status === 403) {
        setDailyLimitError(true);
        return;
      }

      setStats(prev => ({
        correct: isCorrect ? prev.correct + 1 : prev.correct,
        total: prev.total + 1,
        streak: isCorrect ? prev.streak + 1 : 0,
      }));

      const savedCategoryStats = localStorage.getItem('sessionCategoryStats');
      let categoryStats = savedCategoryStats ? JSON.parse(savedCategoryStats) : {};

      if (!categoryStats[questionCategory]) {
        categoryStats[questionCategory] = { correct: 0, total: 0 };
      }
      categoryStats[questionCategory].total += 1;
      if (isCorrect) {
        categoryStats[questionCategory].correct += 1;
      }
      localStorage.setItem('sessionCategoryStats', JSON.stringify(categoryStats));
    }
  };

  const handleNext = () => {
    window.scrollTo(0, 0);
    
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setAnswered(false);
      setSelectedAnswer(null);
      setTimeElapsed(0);
    } else {
      loadQuestions();
      setCurrentIndex(0);
      setAnswered(false);
      setSelectedAnswer(null);
      setTimeElapsed(0);
    }
  };

  const handleReportIssue = async () => {
    const question = questions[currentIndex];
    const reason = prompt('What is the issue?\n1. Wrong answer\n2. Too ambiguous\n3. Correct answer not displayed\n\nEnter 1, 2, or 3:');
    
    const reasonMap = {
      '1': 'incorrect',
      '2': 'ambiguous',
      '3': 'missing_correct'
    };
    
    if (!reasonMap[reason]) return;
    
    const details = prompt('Please provide additional details (optional):') || '';
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questions/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          question_id: question.id,
          user_id: '',
          reason: reasonMap[reason],
          details: details,
          timestamp: new Date().toISOString(),
          resolved: false
        })
      });
      
      const data = await response.json();
      
      const emailSubject = encodeURIComponent(`Question Report: ${question.id}`);
      const emailBody = encodeURIComponent(
        `Question ID: ${question.id}\n` +
        `Question: ${question.question}\n` +
        `Issue Type: ${reasonMap[reason]}\n` +
        `Details: ${details}\n` +
        `Reported At: ${new Date().toISOString()}`
      );
      window.open(`mailto:${supportEmail}?subject=${emailSubject}&body=${emailBody}`, '_blank');
      
      if (data.quarantined) {
        alert('Thank you! This question has been quarantined for review.');
        const newQuestions = questions.filter((_, idx) => idx !== currentIndex);
        setQuestions(newQuestions);
        if (currentIndex >= newQuestions.length) {
          setCurrentIndex(Math.max(0, newQuestions.length - 1));
        }
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error reporting question:', error);
      alert('Report saved.');
    }
  };

  const handleFinishSession = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session/finish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const data = await response.json();

      localStorage.removeItem('sessionStats');
      localStorage.removeItem('sessionCategoryStats');
      setStats({ correct: 0, total: 0, streak: 0 });

      if (data.session_data) {
        setSessionSummaryData(data.session_data);
      } else {
        setSessionSummaryData({
          questions_answered: stats.total,
          correct_answers: stats.correct,
          accuracy: stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0,
          current_streak: stats.streak,
          highest_streak: 0,
          day_streak: 1,
          time_spent: 0,
          categories_studied: [],
          improvement: 0,
        });
      }
      setShowSessionSummary(true);
    } catch (error) {
      console.error('Error finishing session:', error);
      const currentStats = { ...stats };
      localStorage.removeItem('sessionStats');
      localStorage.removeItem('sessionCategoryStats');
      setStats({ correct: 0, total: 0, streak: 0 });

      setSessionSummaryData({
        questions_answered: currentStats.total,
        correct_answers: currentStats.correct,
        accuracy: currentStats.total > 0 ? Math.round((currentStats.correct / currentStats.total) * 100) : 0,
        current_streak: currentStats.streak || 0,
        highest_streak: 0,
        day_streak: 1,
        time_spent: 0,
        categories_studied: [],
        improvement: 0,
      });
      setShowSessionSummary(true);
    }
  };

  // Render filter panel as a function (NOT a component) to prevent
  // React from unmounting/remounting it on every re-render, which
  // was destroying the category dropdown scroll position
  const renderFilterPanel = (isMobile = false) => (
    <div className={`${isMobile ? 'p-4' : 'bg-white rounded-xl shadow-lg p-3 border-2 border-gray-100'} flex flex-col`}>
      {!isMobile && <h3 className="text-base font-bold text-gray-900 mb-3 pb-2 border-b-2 border-cyan-500">Filters</h3>}

      {/* AI-Guided Badge — top of card, turquoise 3D branding */}
      <div className="mb-3">
        <div className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border-2 ${
          aiStatus === 'active'
            ? 'border-cyan-600'
            : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'
        }`} style={aiStatus === 'active' ? {
          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)',
          boxShadow: '0 6px 16px rgba(6, 182, 212, 0.4), 0 3px 6px rgba(6, 182, 212, 0.3), inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -2px 0 rgba(0,0,0,0.1)',
          borderColor: '#0e7490',
        } : {
          boxShadow: '0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.5)'
        }}>
          {/* Status dot */}
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
            aiStatus === 'active'
              ? 'bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)]'
              : 'bg-gray-300'
          }`} />
          <div className="flex-1 min-w-0">
            <span className={`text-sm font-bold ${
              aiStatus === 'active' ? 'text-white' : 'text-gray-500'
            }`}>
              AI-Guided
            </span>
            {aiStatus === 'diagnostics_incomplete' && (
              <p className="text-xs text-gray-400 leading-tight mt-0.5">
                Complete diagnostic sessions to unlock
                {diagnosticProgress.required > 0 && (
                  <span className="block text-xs text-cyan-600 font-semibold mt-0.5">
                    {Math.min(Math.floor(diagnosticProgress.answered / (diagnosticProgress.required / 3)), 3)}/3 sessions done
                  </span>
                )}
              </p>
            )}
            {aiStatus === 'plan_required' && (
              <p className="text-xs text-gray-400 leading-tight mt-0.5">
                <a href="/subscription" className="text-cyan-600 hover:underline font-semibold">Upgrade plan</a> to unlock AI guidance
              </p>
            )}
            {aiStatus === 'active' && (
              <p className="text-xs text-cyan-100 leading-tight mt-0.5">
                Selecting questions based on your learning profile
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-2 space-y-1">
        {dailyLimitStatus && dailyLimitStatus.daily_limit !== -1 && (
          <div className="flex items-center justify-between text-sm bg-cyan-100 border-2 border-cyan-700 rounded-xl px-3 py-3">
            <span className="text-cyan-800 font-bold">
              {dailyLimitStatus.questions_remaining}/{dailyLimitStatus.daily_limit} today
            </span>
            <a href="/subscription" className="text-cyan-700 font-black hover:underline bg-cyan-200 px-2 py-1 rounded-xl border-2 border-cyan-700">UNLOCK</a>
          </div>
        )}
      </div>
      
      <div className="space-y-3">
        {/* Source Filter - Checkbox style dropdown */}
        <div>
          <label className="block text-xs font-bold text-gray-800 mb-1">SOURCE</label>
          <div className="relative filter-dropdown">
            <button
              onClick={() => hasPaidSubscription && setShowSourceDropdown(!showSourceDropdown)}
              className={`w-full px-3 py-3 border-2 rounded-xl text-sm font-medium text-left flex justify-between items-center ${
                hasPaidSubscription 
                  ? 'border-gray-300 bg-gray-50 cursor-pointer' 
                  : 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
              }`}
              disabled={!hasPaidSubscription}
            >
              <span>
                {sourceFilter === 'all' ? 'All Questions' :
                 sourceFilter === 'une_priority' ? 'UNE Priority' :
                 sourceFilter === 'mcq_database' ? 'General Bank' :
                 sourceFilter === 'imported' ? 'My Uploaded' :
                 sourceFilter === 'shared' ? 'Shared Library' :
                 sourceFilter === 'sample' ? 'Sample Questions' : 'All Questions'}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showSourceDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showSourceDropdown && hasPaidSubscription && (
              <div className="absolute z-50 mt-1 w-full bg-white border-2 border-gray-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {[
                  { value: 'all', label: 'All Questions' },
                  { value: 'une_priority', label: 'UNE Priority' },
                  { value: 'mcq_database', label: 'General Bank' },
                  { value: 'imported', label: 'My Uploaded' },
                  { value: 'shared', label: 'Shared Library' },
                  { value: 'sample', label: 'Sample Questions' }
                ].map(option => (
                  <div 
                    key={option.value}
                    className="flex items-center px-3 py-3 hover:bg-gray-50 cursor-pointer active:bg-gray-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => { setSourceFilter(option.value); setShowSourceDropdown(false); }}
                  >
                    <input
                      type="radio"
                      checked={sourceFilter === option.value}
                      onChange={() => {}}
                      className="mr-3 w-5 h-5"
                    />
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {!hasPaidSubscription && (
            <p className="text-xs text-gray-500 mt-1">
              <a href="/subscription" className="text-blue-600 hover:underline">Upgrade</a> to filter by source
            </p>
          )}
        </div>

        {/* Question Type (Scenario) Filter */}
        <div>
          <label className="block text-xs font-bold text-gray-800 mb-1">QUESTION TYPE</label>
          <div className="relative filter-dropdown">
            <button
              onClick={() => setShowScenarioDropdown(!showScenarioDropdown)}
              className="w-full px-3 py-3 border-2 border-amber-300 rounded-xl text-sm font-medium bg-amber-50 text-left flex justify-between items-center"
            >
              <span>
                {scenarioType === 'all' ? 'All Questions' :
                 scenarioType === 'scenario_only' ? 'Scenarios Only' :
                 'Theory Only'}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showScenarioDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showScenarioDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border-2 border-amber-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {[
                  { value: 'all', label: 'All Questions', desc: 'Theory and clinical scenarios' },
                  { value: 'scenario_only', label: 'Scenarios Only', desc: 'Clinical vignettes and case-based' },
                  { value: 'theory_only', label: 'Theory Only', desc: 'Conceptual and recall questions' }
                ].map(option => (
                  <div
                    key={option.value}
                    className="flex items-center px-3 py-3 hover:bg-amber-50 cursor-pointer active:bg-amber-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => { setScenarioType(option.value); setShowScenarioDropdown(false); }}
                  >
                    <input
                      type="radio"
                      checked={scenarioType === option.value}
                      onChange={() => {}}
                      className="mr-3 w-5 h-5 accent-amber-500"
                    />
                    <div>
                      <span className="text-sm font-medium">{option.label}</span>
                      <p className="text-xs text-gray-400">{option.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Year Filter - Checkbox style dropdown */}
        <div>
          <label className="block text-xs font-bold text-gray-800 mb-1">YEAR</label>
          <div className="relative filter-dropdown">
            <button
              onClick={() => setShowYearDropdown(!showYearDropdown)}
              className="w-full px-3 py-3 border-2 border-blue-300 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-50 to-purple-50 text-left flex justify-between items-center"
            >
              <span>
                {year === 'all' ? 'All Years' : `Year ${year}`}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showYearDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showYearDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border-2 border-blue-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {[
                  { value: 'all', label: 'All Years' },
                  { value: '1', label: 'Year 1' },
                  { value: '2', label: 'Year 2' },
                  { value: '3', label: 'Year 3' },
                  { value: '4', label: 'Year 4' },
                  { value: '5', label: 'Year 5' },
                  { value: '6', label: 'Year 6' }
                ].map(option => (
                  <div 
                    key={option.value}
                    className="flex items-center px-3 py-3 hover:bg-blue-50 cursor-pointer active:bg-blue-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => { setYear(option.value); setShowYearDropdown(false); }}
                  >
                    <input
                      type="radio"
                      checked={year === option.value}
                      onChange={() => {}}
                      className="mr-3 w-5 h-5 accent-blue-500"
                    />
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-800 mb-1">
            CATEGORIES {selectedCategories.length > 0 && `(${selectedCategories.length})`}
          </label>
          <div className="relative filter-dropdown">
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full px-3 py-3 border-2 border-green-300 rounded-xl text-sm font-medium bg-green-50 text-left flex justify-between items-center"
            >
              <span>
                {selectedCategories.length === 0 
                  ? 'All Categories' 
                  : `${selectedCategories.length} selected`}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showCategoryDropdown && (
              <div ref={categoryDropdownListRef} className={`${isMobile ? 'mt-1 w-full' : 'absolute z-50 mt-1 w-full max-h-72 overflow-y-auto shadow-lg filter-dropdown'} bg-white border-2 border-green-300 rounded-xl`}>
                <div className="p-2 border-b sticky top-0 bg-white flex justify-between items-center">
                  <button onClick={handleClearCategories} className="text-xs text-blue-600 hover:underline font-semibold">
                    Clear All
                  </button>
                </div>
                {CATEGORIES.map(cat => (
                  <div key={cat.value} className="border-b border-gray-100 last:border-b-0">
                    <div 
                      className="flex items-center px-3 py-3 hover:bg-green-50 cursor-pointer active:bg-green-100"
                      onClick={() => handleCategoryToggle(cat.value)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.value)}
                        onChange={() => {}}
                        className="mr-3 w-5 h-5 accent-green-500"
                      />
                      <span className="text-sm font-medium flex-1">{cat.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Complexity Filter - Checkbox style dropdown */}
        <div>
          <label className="block text-xs font-bold text-gray-800 mb-1">COMPLEXITY</label>
          <div className="relative filter-dropdown">
            <button
              onClick={() => setShowComplexityDropdown(!showComplexityDropdown)}
              className="w-full px-3 py-3 border-2 border-purple-300 rounded-xl text-sm font-medium bg-purple-50 text-left flex justify-between items-center"
            >
              <span>
                {difficulty === 'all' ? 'All Levels' : `${difficulty} - ${complexityLabels[difficulty]}`}
              </span>
              <svg className={`w-4 h-4 transition-transform ${showComplexityDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showComplexityDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border-2 border-purple-300 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {[
                  { value: 'all', label: 'All Levels' },
                  { value: '1', label: '1 - Foundational' },
                  { value: '2', label: '2 - Competent' },
                  { value: '3', label: '3 - Proficient' },
                  { value: '4', label: '4 - Advanced' }
                ].map(option => (
                  <div 
                    key={option.value}
                    className="flex items-center px-3 py-3 hover:bg-purple-50 cursor-pointer active:bg-purple-100 border-b border-gray-100 last:border-b-0"
                    onClick={() => { setDifficulty(option.value); setShowComplexityDropdown(false); }}
                  >
                    <input
                      type="radio"
                      checked={difficulty === option.value}
                      onChange={() => {}}
                      className="mr-3 w-5 h-5 accent-purple-500"
                    />
                    <span className="text-sm font-medium">{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Wrong Answer Review Mode Banner */}
      {isWrongReviewMode && (
        <div className="mt-2 mb-1">
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300">
            <span className="text-lg">🎯</span>
            <div>
              <span className="text-xs font-bold text-amber-700">Practising Wrong Answers</span>
              <p className="text-xs text-amber-600 leading-tight mt-0.5">These questions need another go — you've got this!</p>
            </div>
          </div>
        </div>
      )}

      {/* Session Stats - positioned higher with reduced spacing */}
      <div className="mt-1 pt-1 border-t-2 border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3">
          <h4 className="text-xs font-bold text-gray-700 mb-2">SESSION STATS</h4>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-600">{stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%</div>
              <div className="text-xs text-gray-500">Accuracy</div>
            </div>
            <div>
              <div className="text-xl font-bold text-purple-600">{stats.streak}</div>
              <div className="text-xs text-gray-500">Streak</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const question = questions.length > 0 ? questions[currentIndex] : null;

  // Modified finish session to show stats first on mobile
  const handleFinishSessionClick = () => {
    // On mobile, show stats modal first
    if (window.innerWidth < 1024) {
      setShowStatsModal(true);
    } else {
      // On desktop, proceed directly
      handleFinishSession();
    }
  };
  
  const confirmFinishSession = () => {
    setShowStatsModal(false);
    handleFinishSession();
  };

  return (
    <Layout>
      <div className="w-full max-w-7xl mx-auto pt-4 overflow-x-hidden" style={{ maxWidth: '100vw' }}>
        {/* Session Stats Modal for Mobile */}
        {showStatsModal && (
          <>
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowStatsModal(false)}
            />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl z-50 w-80 p-6">
              <h3 className="text-xl font-bold text-gray-800 text-center mb-4">Session Stats</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{stats.total}</div>
                  <div className="text-sm text-gray-500">Questions</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%</div>
                  <div className="text-sm text-gray-500">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">{stats.streak}</div>
                  <div className="text-sm text-gray-500">Streak</div>
                </div>
              </div>
              <div className="text-center mb-4">
                <p className="text-gray-600 text-sm">Correct: {stats.correct} / {stats.total}</p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmFinishSession}
                  className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700"
                >
                  Finish & View Analytics
                </button>
                <button
                  onClick={() => setShowStatsModal(false)}
                  className="w-full py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300"
                >
                  Continue Practice
                </button>
              </div>
            </div>
          </>
        )}

        {/* Mobile Filter Drawer */}
        {mobileFiltersOpen && (
          <>
            <div 
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setMobileFiltersOpen(false)}
            />
            
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl z-50 max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-white pt-3 pb-2 border-b border-gray-200 z-10">
                <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
                <div className="flex items-center justify-between px-4">
                  <h3 className="text-lg font-bold text-gray-900">Filters</h3>
                  <button
                    onClick={() => setMobileFiltersOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {renderFilterPanel(true)}
              
              <div className="sticky bottom-0 p-4 bg-white border-t border-gray-200">
                <button
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full py-4 bg-cyan-500 text-white rounded-xl font-bold text-lg active:bg-blue-700"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 w-full overflow-x-hidden items-start">
          {/* Desktop Sidebar - Fixed position relative to viewport */}
          <div className="hidden lg:block lg:col-span-1 self-start">
            <div className="sticky top-16">
              {renderFilterPanel()}
            </div>
          </div>

          {/* Main Content - Question Card */}
          <div className="lg:col-span-4 w-full overflow-x-hidden">
            {/* Loading / Error / Empty states — shown inline so filters stay visible */}
            {loading ? (
              <div className="bg-white rounded-xl border-2 border-gray-100 w-full overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading questions...</p>
                  </div>
                </div>
              </div>
            ) : dailyLimitError ? (
              <div className="bg-white rounded-xl border-2 border-gray-100 w-full overflow-hidden p-6 sm:p-8 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-6xl mb-4">⏰</div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Daily Limit Reached</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base">
                  Upgrade your subscription to get unlimited access!
                </p>
                <div className="flex flex-col gap-3 max-w-xs mx-auto">
                  <button
                    onClick={() => window.location.href = '/subscription'}
                    className="w-full px-6 py-4 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-colors font-bold"
                  >
                    Upgrade Plan
                  </button>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="w-full px-6 py-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    ← Go to Home Page
                  </button>
                </div>
              </div>
            ) : !question ? (
              <div className="bg-white rounded-xl border-2 border-gray-100 w-full overflow-hidden p-6 sm:p-8 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-6xl mb-4">📚</div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">No Questions Available</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base">
                  Try adjusting your filters or load sample questions.
                </p>
                <div className="flex flex-col gap-3 max-w-xs mx-auto">
                  <button
                    onClick={() => { setSelectedCategories([]); setYear('all'); setDifficulty('all'); }}
                    className="w-full px-6 py-4 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Clear Filters
                  </button>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="w-full px-6 py-4 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 transition-colors"
                  >
                    ← Go to Home Page
                  </button>
                </div>
              </div>
            ) : (
            <div className="bg-white rounded-xl border-2 border-gray-100 w-full overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
              {/* Question Header - White sub-header with tags, progress, and timer */}
              <div
                ref={questionHeaderRef}
                className="bg-white border-b border-slate-200 px-4 py-4"
              >
                {/* Mobile: Stacked layout */}
                <div className="flex flex-col gap-3 lg:hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-cyan-600">Q{currentIndex + 1}</span>
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full border-2" style={{ backgroundColor: 'rgb(219, 234, 254)', borderColor: 'rgb(147, 197, 253)', color: 'rgb(29, 78, 216)' }}>Year {question.year}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-lg font-bold text-slate-700">
                        {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full border-2 capitalize" style={{ backgroundColor: 'rgb(220, 252, 231)', borderColor: 'rgb(134, 239, 172)', color: 'rgb(21, 128, 61)' }}>{question.category}</span>
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full border-2" style={{ backgroundColor: 'rgb(243, 232, 255)', borderColor: 'rgb(216, 180, 254)', color: 'rgb(126, 34, 206)' }}>{complexityLabels[question.difficulty] || `Level ${question.difficulty}`}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{currentIndex + 1}/{questions.length}</span>
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 rounded-full transition-all duration-300"
                          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Desktop: Single row layout */}
                <div className="hidden lg:flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-cyan-600">Q{currentIndex + 1}</span>
                    <span className="px-3 py-1.5 text-sm font-medium rounded-full border-2" style={{ backgroundColor: 'rgb(219, 234, 254)', borderColor: 'rgb(147, 197, 253)', color: 'rgb(29, 78, 216)' }}>Year {question.year}</span>
                    <span className="px-3 py-1.5 text-sm font-medium rounded-full border-2 capitalize" style={{ backgroundColor: 'rgb(220, 252, 231)', borderColor: 'rgb(134, 239, 172)', color: 'rgb(21, 128, 61)' }}>{question.category}</span>
                    <span className="px-3 py-1.5 text-sm font-medium rounded-full border-2" style={{ backgroundColor: 'rgb(243, 232, 255)', borderColor: 'rgb(216, 180, 254)', color: 'rgb(126, 34, 206)' }}>{complexityLabels[question.difficulty] || `Level ${question.difficulty}`}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">Progress</span>
                      <span className="text-sm font-medium text-slate-700">{currentIndex + 1}/{questions.length}</span>
                      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 rounded-full transition-all duration-300"
                          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-xl font-bold text-slate-700">
                        {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question Body */}
              <div className="p-3 sm:p-4">
                <h2 className="text-[13px] sm:text-[15px] font-semibold mb-4 leading-relaxed" style={{ color: '#1E293B' }}>
                  {question.question}
                </h2>

                {/* Answer Options */}
                <div className="space-y-3 mb-4">
                  {(() => {
                    const validOptions = question.options
                      ?.map((option, originalIndex) => ({ option, originalIndex }))
                      .filter(({ option }) => option && option.trim() && option.toLowerCase() !== 'nan') || [];
                    
                    return validOptions.map(({ option, originalIndex }, displayIndex) => {
                      const isSelected = selectedAnswer === originalIndex;
                      const isCorrect = originalIndex === question.correct_answer;
                      const showResult = answered;

                      let buttonClass = 'w-full text-left px-4 py-2.5 rounded-xl border-2 transition-all duration-200 group ';
                      let buttonStyle = {};

                      if (!showResult) {
                        if (isSelected) {
                          // Selected state - darker grey to match hover, so selection is clearly visible
                          buttonStyle = {
                            background: '#94A3B8',
                            borderColor: '#1E293B'
                          };
                        } else {
                          // Default state - light grey background with always-dark border
                          buttonStyle = {
                            background: '#E0E0E0',
                            borderColor: '#1E293B'
                          };
                        }
                      } else {
                        if (isCorrect) {
                          buttonClass += 'bg-emerald-50 border-emerald-500';
                        } else if (isSelected && !isCorrect) {
                          buttonClass += 'bg-red-50 border-red-500';
                        } else {
                          buttonClass += 'bg-slate-50 border-slate-200';
                        }
                      }

                      // Circle styling - match border color of the answer box
                      let circleClass = 'w-10 h-10 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center font-bold mr-3 flex-shrink-0 text-sm border-2 transition-all duration-200 answer-circle ';
                      let circleStyle = {};
                      if (showResult && isCorrect) {
                        circleClass += 'bg-emerald-500 text-white border-emerald-500';
                      } else if (showResult && isSelected && !isCorrect) {
                        circleClass += 'bg-red-500 text-white border-red-500';
                      } else if (isSelected) {
                        // Selected - darker to match selected button state
                        circleStyle = { backgroundColor: '#94A3B8', borderColor: '#1E293B', color: '#1E293B' };
                      } else {
                        // Default - always dark border/text, always light bg
                        circleStyle = { backgroundColor: '#E0E0E0', borderColor: '#1E293B', color: '#1E293B' };
                      }

                      return (
                        <button
                          key={originalIndex}
                          onClick={() => handleAnswerSelect(originalIndex)}
                          disabled={answered}
                          className={buttonClass}
                          style={!showResult ? buttonStyle : {}}
                          data-testid={`answer-option-${originalIndex}`}
                          onMouseEnter={(e) => {
                            if (!answered) {
                              // Hover state: medium grey background, always-dark border
                              e.currentTarget.style.background = '#94A3B8';
                              e.currentTarget.style.borderColor = '#1E293B';
                              // Circle: bg stays LIGHT (#E0E0E0), border+text always DARK
                              const circle = e.currentTarget.querySelector('.answer-circle');
                              if (circle) {
                                circle.style.backgroundColor = '#E0E0E0';
                                circle.style.borderColor = '#1E293B';
                                circle.style.color = '#1E293B';
                              }
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!answered) {
                              // If selected, return to selected state; otherwise return to default
                              const resetBg = isSelected ? '#94A3B8' : '#E0E0E0';
                              e.currentTarget.style.background = resetBg;
                              e.currentTarget.style.borderColor = '#1E293B'; // always dark
                              const circle = e.currentTarget.querySelector('.answer-circle');
                              if (circle) {
                                circle.style.backgroundColor = '#E0E0E0'; // always light
                                circle.style.borderColor = '#1E293B'; // always dark
                                circle.style.color = '#1E293B'; // always dark
                              }
                            }
                          }}
                        >
                          <div className="flex items-center">
                            <span className={circleClass} style={circleStyle}>
                              {String.fromCharCode(65 + displayIndex)}
                            </span>
                            <span className="flex-1 text-[12px] sm:text-[14px] font-medium leading-relaxed" style={{ color: '#1E293B' }}>{option}</span>
                            {showResult && isCorrect && (
                              <span className="text-green-600 text-xl ml-2">✓</span>
                            )}
                            {showResult && isSelected && !isCorrect && (
                              <span className="text-red-600 text-xl ml-2">✗</span>
                            )}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>

                {/* Submit Answer — appears after selecting an answer, before submitting */}
                {selectedAnswer !== null && !answered && (
                  <div className="pt-3" ref={submitBtnRef}>
                    <button
                      onClick={handleSubmitAnswer}
                      className="w-full lg:w-auto px-8 py-3 rounded-xl font-bold text-white text-sm bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 transition-all"
                      style={{ boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      Submit Answer
                    </button>
                  </div>
                )}

                {/* Action Buttons - Next Question, Finish Session, Report Issue */}
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-3 pt-2">
                  {/* Next Question Button - Same width as filter dropdowns on PC */}
                  <button
                    onClick={handleNext}
                    disabled={!answered}
                    className={`w-full lg:w-48 px-3 py-3 rounded-xl transition-all font-bold flex items-center justify-center text-sm ${
                      answered
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                        : 'text-slate-400 bg-slate-200 cursor-not-allowed'
                    }`}
                    style={answered ? { boxShadow: 'var(--shadow-3d-btn)' } : {}}
                  >
                    {currentIndex < questions.length - 1 ? 'Next Question →' : 'Load More →'}
                  </button>

                  {/* Finish Session Button - Same width as filter dropdowns on PC */}
                  <button
                    onClick={handleFinishSessionClick}
                    className="w-full lg:w-48 px-3 py-3 text-white rounded-xl font-bold text-sm relative overflow-hidden"
                    style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                    <span className="relative z-10">Finish Session</span>
                  </button>

                  {/* Report Issue Button - Same width as filter dropdowns on PC */}
                  <button
                    onClick={handleReportIssue}
                    className="w-full lg:w-48 px-3 py-3 text-white rounded-xl font-medium text-sm"
                    style={{ background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                  >
                    🚩 Report Issue
                  </button>
                </div>

                {/* Explanation - BELOW Action Buttons */}
                {answered && question.explanation && (
                  <div className="p-3 sm:p-4 rounded-r-xl mt-4" style={{ backgroundColor: '#E0E0E0', borderLeft: '4px solid #1E293B' }}>
                    <h4 className="font-bold mb-2 text-sm sm:text-base" style={{ color: '#1E293B' }}>Explanation:</h4>
                    <p className="text-sm sm:text-base" style={{ color: '#1E293B' }}>{question.explanation}</p>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      {/* ── Session Summary Celebration Modal ── */}
      {showSessionSummary && sessionSummaryData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in zoom-in">
            {/* Confetti-style top bar */}
            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-cyan-400 via-emerald-400 to-amber-400 rounded-t-2xl" />

            {/* Header */}
            <div className="text-center mt-2 mb-4">
              <div className="text-4xl mb-2">
                {sessionSummaryData.accuracy >= 80 ? '🌟' : sessionSummaryData.accuracy >= 60 ? '💪' : '📚'}
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {sessionSummaryData.accuracy >= 80 ? 'Fantastic Work!' : sessionSummaryData.accuracy >= 60 ? 'Great Session!' : 'Well Done!'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {sessionSummaryData.accuracy >= 80
                  ? 'You\'re really mastering this material!'
                  : sessionSummaryData.accuracy >= 60
                  ? 'Solid progress — keep building on this!'
                  : 'Every question brings you closer to your goal!'}
              </p>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-cyan-50 rounded-xl p-3 text-center border border-cyan-100">
                <div className="text-2xl font-bold text-cyan-700">{sessionSummaryData.correct_answers}/{sessionSummaryData.questions_answered}</div>
                <div className="text-xs text-cyan-600 font-medium">Correct</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                <div className="text-2xl font-bold text-emerald-700">{sessionSummaryData.accuracy}%</div>
                <div className="text-xs text-emerald-600 font-medium">Accuracy</div>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                <div className="text-2xl font-bold text-amber-700 flex items-center justify-center gap-1">
                  🔥 {sessionSummaryData.day_streak || 1}
                </div>
                <div className="text-xs text-amber-600 font-medium">Day Streak</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-3 text-center border border-purple-100">
                <div className="text-2xl font-bold text-purple-700">
                  {sessionSummaryData.time_spent > 0
                    ? `${Math.floor(sessionSummaryData.time_spent / 60)}m`
                    : '—'}
                </div>
                <div className="text-xs text-purple-600 font-medium">Time Spent</div>
              </div>
            </div>

            {/* Improvement indicator */}
            {sessionSummaryData.improvement !== 0 && (
              <div className={`text-center text-sm font-medium mb-3 px-3 py-1.5 rounded-lg ${
                sessionSummaryData.improvement > 0
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {sessionSummaryData.improvement > 0
                  ? `📈 ${sessionSummaryData.improvement}% above your average — brilliant!`
                  : `💡 Keep going — consistency is the key to mastery!`}
              </div>
            )}

            {/* Categories studied */}
            {sessionSummaryData.categories_studied && sessionSummaryData.categories_studied.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-medium text-gray-500 mb-1.5">Topics covered</div>
                <div className="flex flex-wrap gap-1.5">
                  {sessionSummaryData.categories_studied.map((cat, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full border border-gray-200">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Answer streak */}
            {sessionSummaryData.current_streak > 0 && (
              <div className="text-center text-sm text-gray-500 mb-4">
                ⚡ Best answer streak this session: <span className="font-bold text-cyan-600">{sessionSummaryData.current_streak}</span>
                {sessionSummaryData.current_streak >= (sessionSummaryData.highest_streak || 0) && sessionSummaryData.current_streak > 3
                  ? ' — New personal best! 🎉'
                  : ''}
              </div>
            )}

            {/* Share buttons */}
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-500 mb-2 text-center">Share your progress</div>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => {
                    const text = `I just scored ${sessionSummaryData.accuracy}% on ${sessionSummaryData.questions_answered} medical MCQs! Can you beat my score?`;
                    const url = window.location.origin;
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'noopener');
                  }}
                  className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                  title="Share on X"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  Post
                </button>
                <button
                  onClick={() => {
                    const text = `I just scored ${sessionSummaryData.accuracy}% on ${sessionSummaryData.questions_answered} medical MCQs! Can you beat my score?`;
                    const url = window.location.origin;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'noopener');
                  }}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                  title="Share on Facebook"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Share
                </button>
                <button
                  onClick={() => {
                    const text = `I just scored ${sessionSummaryData.accuracy}% on ${sessionSummaryData.questions_answered} medical MCQs! Can you beat my score? ${window.location.origin}`;
                    navigator.clipboard.writeText(text).then(() => {
                      const btn = document.activeElement;
                      const orig = btn.textContent;
                      btn.textContent = 'Copied!';
                      setTimeout(() => { btn.textContent = orig; }, 2000);
                    });
                  }}
                  className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-2 px-3 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                  Copy
                </button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSessionSummary(false);
                  window.location.href = '/analytics';
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-4 rounded-xl transition-colors text-sm"
              >
                View Analytics
              </button>
              <button
                onClick={() => {
                  setShowSessionSummary(false);
                  window.location.reload();
                }}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-colors text-sm shadow-md"
              >
                Study More
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default QuestionsPage;
